import type { DatadogAiComponents } from "@p0security/integrations-datadog-ai-shared/types";
import {
  type FrontendInstaller,
  otherInstalledIntegration,
} from "@p0security/integrations-installer";
import type { ItemConfigOf } from "@p0security/shared/install/types";
import { CommandDisplay, Typography } from "@p0security/ui";
import type React from "react";

import {
  type InstallContext,
  awsStoreAdminKeys,
  awsTf,
  gcpStoreAdminKeys,
  gcpTf,
} from "./iac.ts";

type SecretManagerConfig = ItemConfigOf<
  DatadogAiComponents["credential"]
>["secretManager"];

type SecretManagerType = SecretManagerConfig["type"];

type InstallCommands = ReturnType<
  NonNullable<
    NonNullable<
      FrontendInstaller<DatadogAiComponents>["components"]["credential"]
    >["instructions"]
  >
>;

/** What a secret manager contributes to the credential install step. Methods,
 * not properties, let an implementation narrow to its own member. */
type CredentialIac<T extends SecretManagerType = SecretManagerType> = {
  instructions(
    context: InstallContext,
    organization: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): InstallCommands;
  /** The command that stores the admin keys, if this secret manager has one */
  storeAdminKeys(
    organization: string,
    secretManager: Extract<SecretManagerConfig, { type: T }>
  ): string | undefined;
};

const gcpSmIac: CredentialIac = {
  instructions: (context, organization, secretManager) => ({
    commands: {
      iac: [
        {
          header:
            'Apply the following terraform to deploy P0\'s Datadog connector, then click "Next".',
          command: gcpTf(context, organization, secretManager),
        },
      ],
    },
  }),
  storeAdminKeys: gcpStoreAdminKeys,
} satisfies CredentialIac<"gcp-sm">;

const awsSmIac: CredentialIac = {
  instructions: (context, organization, secretManager) => ({
    commands: {
      iac: [
        {
          header:
            'Apply the following terraform to deploy P0\'s Datadog connector, then click "Next".',
          command: awsTf(context, organization, secretManager),
        },
      ],
    },
  }),
  storeAdminKeys: awsStoreAdminKeys,
} satisfies CredentialIac<"aws-sm">;

// The Azure Key Vault option is hidden, and the backend refuses it, until the
// connector runs on Azure.
const azureKvIac: CredentialIac = {
  instructions: () => ({ commands: {} }),
  storeAdminKeys: () => undefined,
} satisfies CredentialIac<"azure-kv">;

const credentialIac: Record<SecretManagerType, CredentialIac> = {
  "aws-sm": awsSmIac,
  "azure-kv": azureKvIac,
  "gcp-sm": gcpSmIac,
};

const { Paragraph } = Typography;

const ServiceAccountSetup: React.FC<{
  id: string;
  secretManager: SecretManagerConfig;
}> = ({ id, secretManager }) => {
  const storeAdminKeys = credentialIac[secretManager.type].storeAdminKeys(
    id,
    secretManager
  );
  return (
    <div>
      <Paragraph type="secondary">
        In Datadog, create a service account for P0 under Organization Settings
        &gt; Service Accounts.
      </Paragraph>
      <Paragraph type="secondary">
        Give it a role that holds only the permissions that machines may
        request, including MCP Read for the Datadog MCP server. P0&apos;s tokens
        can hold no permission that this role lacks.
      </Paragraph>
      {storeAdminKeys && (
        <>
          <Paragraph>Admin keys</Paragraph>
          <Paragraph type="secondary">
            Set <code>DD_API_KEY</code> and <code>DD_APP_KEY</code> to a Datadog
            API key and application key, and run this command to update the
            secret to the keys. The application key&apos;s user needs the
            Service Account Write permission. The connector cannot create tokens
            until the secret holds the keys.
          </Paragraph>
          <CommandDisplay commands={storeAdminKeys} />
        </>
      )}
      <Paragraph>Service account ID</Paragraph>
    </div>
  );
};

export const datadogAiInstaller: FrontendInstaller<DatadogAiComponents> = {
  components: {
    credential: {
      instructions: (context, id, item) =>
        credentialIac[item.secretManager.type].instructions(
          context,
          id,
          item.secretManager
        ),
      items: {
        serviceAccountId: {
          labeler: (_, id, { secretManager, state }) =>
            state === "configure" && (
              <ServiceAccountSetup id={id} secretManager={secretManager} />
            ),
        },
        secretManager: {
          options: {
            "aws-sm": {
              items: {
                install: {
                  optionProvider: otherInstalledIntegration({
                    integration: "aws-sm",
                    component: "iam-write",
                  }),
                },
              },
            },
            "gcp-sm": {
              items: {
                install: {
                  optionProvider: otherInstalledIntegration({
                    integration: "gcp-sm",
                    component: "iam-write",
                  }),
                },
              },
            },
          },
        },
      },
    },
  },
};
